import { Colors, Icon, MultilineInput } from '@amelio/ui-library';
import { useEffect, useRef } from 'react';

import { useSurveyQuestionMotion } from '../hooks/useSurveyQuestionMotion';
import { advancesOnAnswer, advancesOnCommit } from '../surveyQuestionMotion';
import { AssessmentQuestionNote } from './AssessmentQuestionNote';

import type { User } from '@/types/directory/User';
import type { ReviewQuestion } from '@/types/review/ReviewQuestion';

import { KeyboardFocus } from '@/constants/KeyboardFocus';
import { MANAGER_ASSESSMENT_SURVEY_IMAGE_URL } from '@/constants/managerAssessment';
import { QuestionType } from '@/constants/QuestionType';
import { useAmelioTranslations } from '@/hooks';
import { Answer } from '@/pages/SurveyAnswer/SurveyAnswer/components/Answer/Answer';
import { isOtherOptionSelected } from '@/pages/SurveyAnswer/SurveyAnswer/validation';

interface SurveyQuestionProps {
    question: string;
    reviewQuestion: ReviewQuestion;
    reviewee?: User;
    selectedValues: string[];
    onAnswerChange: (values: string[]) => void;
    onNext: () => void;
    onPrevious: () => void;
    onSkip: () => void;
    hasNext: boolean;
    hasPrevious: boolean;
    currentIndex: number;
    totalQuestions: number;
    onDotClick: (index: number) => void;
    answerValues: Record<string, string[]>;
    questionIds: string[];
    /** The assessed employee, whose profile keeps the note written under the question. */
    employeeId?: string;
    cycleInstanceId?: string;
    /** Whether the question is the assessment's own, the only kind a note can be linked to. */
    takesQuestionNotes: boolean;
    otherText: string;
    onOtherTextChange: (value: string) => void;
    /** A completed evaluation is browsed, not answered: only the question navigation stays live. */
    readOnly?: boolean;
    dataTestId?: string;
}

// bg-primary/50 and bg-white/30 measured ~3.1:1 and ~2.7:1 against the dark survey background —
// the answered-dot tone was right at WCAG 1.4.11's 3:1 floor and the unanswered tone missed it.
// /60 and /40 clear it with margin (~3.9:1 and ~3.8:1) without changing which dot reads as which.
const getDotStyles = (isCurrent: boolean, isAnswered: boolean): string => {
    if (isCurrent) {
        return 'w-[32px] bg-primary';
    }
    if (isAnswered) {
        return 'w-[8px] bg-primary/60';
    }
    return 'w-[8px] bg-white/40';
};

export const SurveyQuestion = ({
    question,
    reviewQuestion,
    reviewee,
    selectedValues,
    onAnswerChange,
    onNext,
    onPrevious,
    onSkip,
    hasNext,
    hasPrevious,
    currentIndex,
    totalQuestions,
    onDotClick,
    answerValues,
    questionIds,
    employeeId,
    cycleInstanceId,
    takesQuestionNotes,
    otherText,
    onOtherTextChange,
    readOnly = false,
    dataTestId = 'survey-question',
}: SurveyQuestionProps) => {
    const { t } = useAmelioTranslations();
    const motion = useSurveyQuestionMotion({
        currentIndex,
        onNext,
        onPrevious,
        onSkip,
        onJump: onDotClick,
    });

    const headingRef = useRef<HTMLHeadingElement>(null);
    const hasRenderedRef = useRef(false);

    // The card that had focus leaves with its question; the new question's title takes over so
    // the keyboard lands somewhere that says where it is.
    useEffect(() => {
        if (!hasRenderedRef.current) {
            hasRenderedRef.current = true;
            return;
        }
        headingRef.current?.focus({ preventScroll: true });
    }, [currentIndex]);

    // Section titles are non-answerable headers — they never block navigation.
    const isSectionTitle = reviewQuestion.questionType === QuestionType.SECTION_TITLE;
    // Read-only browsing must reach the questions that were skipped too.
    const canProceed = readOnly || isSectionTitle || selectedValues.length > 0;

    const handleNextClick = () => {
        if (hasNext && canProceed) {
            motion.next();
        }
    };

    /**
     * A single pick is the whole answer, so the survey moves on by itself once the manager has
     * seen it register. Picking "Other" opens a text field, and multi-select, ranking and free
     * text are not finished on the first click: those keep the explicit button.
     */
    const handleAnswerChange = (values: string[]) => {
        if (readOnly) {
            return;
        }
        onAnswerChange(values);

        const movesOnByItself =
            hasNext &&
            values.length > 0 &&
            advancesOnAnswer(reviewQuestion.questionType) &&
            !isOtherOptionSelected(values, reviewQuestion);

        if (movesOnByItself) {
            motion.confirmAnswer();
            return;
        }
        motion.cancelPendingAnswer();
    };

    /** The scale is answered once the finger comes off it, not at every value it passes through. */
    const handleAnswerCommit = () => {
        const settles =
            !readOnly &&
            hasNext &&
            advancesOnCommit(reviewQuestion.questionType) &&
            !isOtherOptionSelected(selectedValues, reviewQuestion);

        if (settles) {
            motion.confirmAnswer();
        }
    };

    const hasMultipleQuestions = totalQuestions > 1;

    return (
        <div
            className={'ds-rounded-lg overflow-hidden border border-gray-200'}
            data-testid={dataTestId}
        >
            <div
                className={'relative w-full bg-linear-to-br from-black via-purple-900/95 to-black'}
            >
                <img
                    src={MANAGER_ASSESSMENT_SURVEY_IMAGE_URL}
                    alt={'Survey question'}
                    className={'absolute inset-0 h-full w-full object-cover opacity-25'}
                />

                <div className={'relative flex min-h-[400px] flex-col justify-between px-8 py-8'}>
                    {/* Keyed on the index so each question is a new node: the incoming half of
                        the slide replays on its own, without a second piece of state. */}
                    <div
                        key={currentIndex}
                        className={`flex flex-1 flex-col items-center justify-center ${motion.className}`}
                        style={motion.style}
                    >
                        <h3
                            ref={headingRef}
                            tabIndex={KeyboardFocus.DISABLED}
                            className={
                                'mt-5 mb-8 max-w-[600px] px-2 text-center text-lg font-medium text-white focus:outline-none sm:text-[25px]'
                            }
                        >
                            {question}
                        </h3>

                        <div className={'ds-gap-md ds-px-xs flex w-full max-w-[550px] flex-col'}>
                            {/* Every question type renders through the shared Answer component so the
                                right widget is used (rating slider, emoji, ranking, yes/no, checkbox,
                                plain text) — identical to the survey-answer flow. */}
                            {/* The shared Answer widgets have no read-only mode of their own: `inert`
                                takes clicks, hover and focus away from all of them at once, without
                                changing the layout (`contents` keeps the parent's gap). */}
                            <div
                                className={'contents'}
                                inert={readOnly}
                                data-testid={`${dataTestId}-answer-area`}
                            >
                                <Answer
                                    question={reviewQuestion}
                                    type={reviewQuestion.questionType}
                                    selectedValues={selectedValues}
                                    onChange={handleAnswerChange}
                                    onCommit={handleAnswerCommit}
                                    onInteractionStart={motion.cancelPendingAnswer}
                                    onEnterPress={handleNextClick}
                                    reviewee={reviewee}
                                />

                                {reviewQuestion.hasOtherOption &&
                                    isOtherOptionSelected(selectedValues, reviewQuestion) && (
                                        <div className={'mt-4 w-full text-white'}>
                                            <MultilineInput
                                                value={otherText}
                                                onChange={onOtherTextChange}
                                                rows={2}
                                                className={
                                                    // border-white/30 measured ~2.7:1 against the dark survey background — under
                                                    // WCAG 1.4.11's 3:1 floor. /40 clears it (~3.8:1).
                                                    'w-full resize-none border-0 border-b border-white/40 bg-transparent px-3 py-2 text-sm text-white placeholder-white/50 focus:border-teal-400 focus:outline-none'
                                                }
                                                placeholder={t('answer.otherOption.placeholder')}
                                                aria-label={t('answer.otherOption.placeholder')}
                                                autoGrow
                                                dataTestId={`${dataTestId}-other-text-input`}
                                                autoFocus={!readOnly}
                                            />
                                        </div>
                                    )}
                            </div>

                            {!isSectionTitle && (
                                // Turning to the note or to "skip" calls off a move armed by a
                                // pick, so the manager is never carried away mid-sentence.
                                <div
                                    className={'mt-4 flex flex-col gap-2'}
                                    onFocusCapture={motion.cancelPendingAnswer}
                                >
                                    {!reviewQuestion.isAnswerMandatory && !readOnly && (
                                        <button
                                            type={'button'}
                                            onClick={motion.skip}
                                            className={
                                                'text-left text-sm !text-[14px] font-normal text-white/60 underline transition-colors hover:text-white'
                                            }
                                            data-testid={'skip-question-button'}
                                        >
                                            {t('managerAssessmentEvaluation.survey.skip')}
                                        </button>
                                    )}

                                    {/* Every answerable question takes a note, whatever its comment setting:
                                        that setting governs the comment of the survey flows, not
                                        the manager's own notes. Keyed by question so moving on
                                        starts from that question's note. */}
                                    {takesQuestionNotes && (
                                        <AssessmentQuestionNote
                                            key={reviewQuestion.id}
                                            employeeId={employeeId}
                                            cycleInstanceId={cycleInstanceId}
                                            questionId={reviewQuestion.id ?? ''}
                                            readOnly={readOnly}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div
                        className={
                            'ds-mt-4xl ds-gap-md ds-pt-4xl flex flex-col border-t border-white/10'
                        }
                    >
                        {hasMultipleQuestions && (
                            <div
                                className={
                                    'scrollbar-glass scrollbar-glass-inverse flex justify-center gap-1.5 overflow-x-auto py-1'
                                }
                            >
                                {Array.from({ length: totalQuestions }).map((_, dotIndex) => {
                                    const isAnswered =
                                        (answerValues[questionIds[dotIndex]]?.length ?? 0) > 0;
                                    const isCurrent = dotIndex === currentIndex;

                                    return (
                                        <button
                                            key={questionIds[dotIndex]}
                                            type={'button'}
                                            onClick={() => motion.jumpTo(dotIndex)}
                                            className={`h-2 shrink-0 cursor-pointer rounded-full transition-all hover:opacity-80 ${getDotStyles(isCurrent, isAnswered)}`}
                                            aria-label={`Question ${dotIndex + 1}`}
                                            data-testid={`question-dot-${dotIndex}`}
                                        />
                                    );
                                })}
                            </div>
                        )}

                        <div
                            className={
                                'ds-gap-md max-md:ds-gap-2xl md:ds-gap-lg flex flex-row items-center justify-between max-md:flex-wrap'
                            }
                        >
                            <button
                                type={'button'}
                                onClick={motion.previous}
                                disabled={!hasPrevious}
                                className={`ds-rounded-sm ds-px-md text-body-sm sm:ds-px-lg sm:text-body flex items-center gap-[6px] py-[10px] font-medium transition-all sm:gap-[8px] ${
                                    hasPrevious
                                        ? 'text-white hover:bg-white/10'
                                        : 'cursor-not-allowed text-white/40'
                                }`}
                                data-testid={'previous-question-button'}
                            >
                                <Icon
                                    name={'arrow-arrow-left-1'}
                                    size={16}
                                    color={hasPrevious ? Colors.WHITE : Colors.BASE_300}
                                />

                                <span className={'whitespace-nowrap'}>
                                    {t('managerAssessmentEvaluation.survey.previousQuestion')}
                                </span>
                            </button>

                            <span className={'text-body-sm font-medium text-white/50'}>
                                {`${currentIndex + 1}/${totalQuestions}`}
                            </span>

                            <button
                                type={'button'}
                                onClick={handleNextClick}
                                disabled={!hasNext || !canProceed}
                                className={`ds-rounded-sm ds-px-md text-body-sm sm:ds-px-lg sm:text-body flex items-center gap-[6px] py-[10px] font-medium transition-all sm:gap-[8px] ${
                                    hasNext && canProceed
                                        ? 'text-navy bg-white shadow-lg hover:bg-white/90'
                                        : 'cursor-not-allowed bg-white/20 text-white/50'
                                }`}
                                data-testid={'next-question-button'}
                            >
                                <span className={'whitespace-nowrap'}>
                                    {t('managerAssessmentEvaluation.survey.nextQuestion')}
                                </span>

                                <Icon
                                    name={'arrow-arrow-right-1'}
                                    size={16}
                                    color={hasNext && canProceed ? Colors.BASE : Colors.BASE_300}
                                />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
